/**
 * Loads OncoEMR tasks for the single-order Tasks tab left navbar.
 * @module useOncoemrTasksList
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchOncoemrTasksByOrder } from "../../../../../api/getCall/fetchOncoemrTasks";
import { OncoemrTaskListItem } from "../../../../../data-model/oncoemrTasksModel";
import { useAbortableFetch } from "../../../../../hooks/useAbortableFetch";
import type { TaskListPanelItem } from "../components/tasksListPanel";
import { mapTaskListItemToPanelItem } from "../helpers/mapTaskListItemToPanelItem";

/** Parameters for {@link useOncoemrTasksList}. */
export interface UseOncoemrTasksListParams {
  orgId: string;
  orderId: string;
}

/**
 * Fetches OncoEMR tasks for one order and maps them for the left list panel.
 *
 * @param params - Org and order scope for the current case.
 * @returns Panel rows, raw API rows, loading and error state, and a refetch helper.
 */
export const useOncoemrTasksList = ({
  orgId,
  orderId,
}: UseOncoemrTasksListParams) => {
  const fetchTasks = useCallback(
    async (signal: AbortSignal) => {
      return fetchOncoemrTasksByOrder(orderId, { org_id: orgId }, signal);
    },
    [orgId, orderId],
  );

  const { runFetch, abort, data, loading, error } =
    useAbortableFetch<OncoemrTaskListItem[]>(fetchTasks);

  const [ackOverrides, setAckOverrides] = useState<
    Record<string, Pick<OncoemrTaskListItem, "is_updated" | "version">>
  >({});

  useEffect(() => {
    if (!orgId || !orderId) {
      return undefined;
    }

    runFetch();
    return () => abort();
  }, [orgId, orderId, runFetch, abort]);

  /**
   * Merges a successful ack response into the cached list without a full refetch.
   *
   * @param taskRowId - `tasks.id` that was acknowledged.
   * @param version - New row version returned by the ack endpoint.
   */
  const applyTaskAck = useCallback((taskRowId: string, version: number) => {
    setAckOverrides((prev) => ({
      ...prev,
      [taskRowId]: { is_updated: false, version },
    }));
  }, []);

  const tasksForOrder = useMemo(() => {
    const rows = data ?? [];
    return rows.map((task) => {
      const override = ackOverrides[task.id];
      return override ? { ...task, ...override } : task;
    });
  }, [data, ackOverrides]);

  const panelItems: TaskListPanelItem[] = useMemo(
    () => tasksForOrder.map(mapTaskListItemToPanelItem),
    [tasksForOrder],
  );

  return {
    panelItems,
    tasksForOrder,
    loading,
    error,
    refetch: runFetch,
    applyTaskAck,
  };
};
